import { Footer } from '@/app/components/Footer'
import { InitialResults } from 'instantsearch.js'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { InstantSearch, InstantSearchSSRProvider } from 'react-instantsearch'
import { getSearchClient } from '../../search/lib/search-client'
import { Header } from '../../shared/components/Header'
import { Newsletter } from '../../shared/components/Newsletter'
import { TopProvidersSection } from '../../shared/components/TopProvidersSection'
import { useLanguageSelection } from '../../shared/providers/LanguageSelectionProvider'
import { CoursesHeader } from '../components'

import { CategoriesShowcase } from '../components/CategoriesShowcase'
import { SubCategoryCoursesHits } from '../components/SubCategoryCoursesHits'
import { SearchInputProvider } from '../contexts/InputSearchContext'

const ITEMS_PER_GROUP = 3
const MAX_GROUPS = 6

type ScopedApiKeys = {
  subcategories_courses: string
  categories: string
}

type ExternalCoursesPageProps = {
  scopedApiKeys: ScopedApiKeys
  initialResults?: InitialResults[]
}

export function ExternalCoursesPage({
  scopedApiKeys,
  initialResults,
}: ExternalCoursesPageProps) {
  const [subcategoriesCoursesInitialResults, categoriesInitialResults] =
    initialResults ?? []
  const t = useTranslations('Courses')
  const { currentLocale } = useLanguageSelection()
  const router = useRouter()

  const onSearchHandler = async (term: string) => {
    await router.push({
      pathname: '/search',
      query: { 'courses[query]': term },
    })
  }

  const client = useMemo(
    () =>
      getSearchClient('', {
        collectionSpecificSearchParameters: {
          subcategories_courses: {
            q: '*',
            group_by: 'subcategory_id',
            group_limit: ITEMS_PER_GROUP,
            include_fields: `$subcategories(name, id, is_promoted, slug) as subcategory,$courses(id,course_id,name_${currentLocale},learning_description_${currentLocale},quality_score,education_provider,instructors,level,min_price) as course`,
            exclude_fields: 'id,course_id,subcategory_id',
            // once we have sufficient promoted categories, we can limit by them and change the sort to randomize them
            // until then, we are limited by the number of sort_by fields TypeSense supports (3)
            sort_by:
              '$subcategories(is_promoted:desc),$courses(quality_score:desc,_rand():desc)',
            limit: MAX_GROUPS,
            query_by: `subcategory_id`,
            'x-typesense-api-key': scopedApiKeys.subcategories_courses,
          },
          categories: {
            sortBy: 'is_promoted:desc,name:asc',
            query_by: `name`,
            limit: 10,
            include_fields: `id,name,slug,$subcategories(id,name,slug)`,
            q: '*',
            'x-typesense-api-key': scopedApiKeys.categories,
          },
        },
      }),
    [currentLocale, scopedApiKeys]
  )

  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <SearchInputProvider>
        <Header className="z-40" />
        <CoursesHeader
          className="md:mx-8"
          onSearch={async (searchTerm) => {
            await onSearchHandler(searchTerm)
          }}
        />
        <InstantSearchSSRProvider
          initialResults={subcategoriesCoursesInitialResults}
        >
          <InstantSearch
            indexName="subcategories_courses"
            searchClient={client}
            numberLocale={currentLocale}
            stalledSearchDelay={500}
            routing={false}
            future={{
              preserveSharedStateOnUnmount: true,
            }}
          >
            <div className="container mb-20 mt-12">
              <SubCategoryCoursesHits
                splitGroupsSize={3}
                className="flex flex-col gap-12"
              >
                <InstantSearchSSRProvider
                  initialResults={categoriesInitialResults}
                >
                  <InstantSearch
                    indexName="categories"
                    searchClient={client}
                    numberLocale={currentLocale}
                    stalledSearchDelay={500}
                    routing={false}
                    future={{
                      preserveSharedStateOnUnmount: true,
                    }}
                  >
                    <CategoriesShowcase />
                  </InstantSearch>
                </InstantSearchSSRProvider>
              </SubCategoryCoursesHits>
            </div>
          </InstantSearch>
        </InstantSearchSSRProvider>
        <TopProvidersSection variant="static" />
        <Newsletter />
        <Footer />
      </SearchInputProvider>
    </>
  )
}
